/* oxlint-disable jsx-a11y/no-static-element-interactions */
/* oxlint-disable jsx-a11y/click-events-have-key-events */

import { useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';

const BREAKPOINT_SM = 640;
const CHAR_WIDTH = 8;
const DROPDOWN_PADDING = 24;
const ICON_SPACE = 20;

const initialState = {
  selectedFareGroupIndex: 0,
  selectedRule: null,
  selectedIndex: null,
  isQuickSelected: false,
  showMobileMenu: false,
  selectedPassengerTypeCode: null,
  showPassengerTypeMenu: false,
};

const modalReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SELECTED_FARE_GROUP_INDEX':
      return { ...state, selectedFareGroupIndex: action.payload };
    case 'SET_SHOW_MOBILE_MENU':
      return { ...state, showMobileMenu: action.payload };
    case 'SET_SHOW_PASSENGER_TYPE_MENU':
      return { ...state, showPassengerTypeMenu: action.payload };
    case 'SET_SELECTED_PASSENGER_TYPE_CODE':
      return { ...state, selectedPassengerTypeCode: action.payload };
    case 'SET_RULE_SELECTION':
      return {
        ...state,
        selectedRule: action.payload.selectedRule,
        selectedIndex: action.payload.selectedIndex,
        isQuickSelected: action.payload.isQuickSelected,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const FlightFareRulesModal = ({ isOpen, onClose, airFareRulesList = [] }) => {
  const [state, dispatch] = useReducer(modalReducer, initialState);

  const {
    selectedFareGroupIndex,
    selectedRule,
    selectedIndex,
    isQuickSelected,
    showMobileMenu,
    selectedPassengerTypeCode,
    showPassengerTypeMenu,
  } = state;

  const contentRef = useRef(null);
  const dropdownRef = useRef(null);
  const selectedItemRef = useRef(null);
  const pendingRuleTitleRef = useRef(null);
  const pendingPassengerTypeCodeRef = useRef(null);

  const currentFareGroup =
    airFareRulesList.length > 0
      ? airFareRulesList[selectedFareGroupIndex] || airFareRulesList[0]
      : null;

  const resetScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }, []);

  const actualPassengerTypeCodes = useMemo(() => {
    if (!currentFareGroup || !currentFareGroup.fareRulesGroups) return [];

    const typesMap = new Map();
    currentFareGroup.fareRulesGroups.forEach((group) => {
      if (group.passengerTypes) {
        group.passengerTypes.forEach((pt) => {
          if (pt && pt.code && pt.name && !typesMap.has(pt.code)) {
            typesMap.set(pt.code, pt);
          }
        });
      }
    });

    return Array.from(typesMap.values());
  }, [currentFareGroup]);

  useEffect(() => {
    if (actualPassengerTypeCodes.length > 0 && !selectedPassengerTypeCode) {
      if (isOpen && !pendingPassengerTypeCodeRef.current) {
        dispatch({
          type: 'SET_SELECTED_PASSENGER_TYPE_CODE',
          payload: actualPassengerTypeCodes[0].code,
        });
      }
    }
  }, [actualPassengerTypeCodes, selectedPassengerTypeCode, isOpen]);

  const selectedGroup = useMemo(() => {
    if (!currentFareGroup || !currentFareGroup.fareRulesGroups || !selectedPassengerTypeCode) {
      return null;
    }

    return (
      currentFareGroup.fareRulesGroups.find(
        (group) =>
          group.passengerTypes &&
          group.passengerTypes.some((pt) => pt && pt.code === selectedPassengerTypeCode),
      ) || null
    );
  }, [currentFareGroup, selectedPassengerTypeCode]);

  const fareRules = useMemo(
    () => (selectedGroup && selectedGroup.fareRuleInfo) || [],
    [selectedGroup],
  );

  const quickRulesContent = useMemo(
    () => (selectedGroup && selectedGroup.quickRules) || null,
    [selectedGroup],
  );

  const closeMobileMenu = useCallback(() => {
    dispatch({ type: 'SET_SHOW_MOBILE_MENU', payload: false });
  }, []);

  const handleRuleClick = useCallback(
    (rule, index) => {
      dispatch({
        type: 'SET_RULE_SELECTION',
        payload: {
          selectedRule: rule,
          selectedIndex: index,
          isQuickSelected: false,
        },
      });
      closeMobileMenu();
      resetScroll();
    },
    [resetScroll, closeMobileMenu],
  );

  const handleQuickRulesClick = useCallback(() => {
    if (!quickRulesContent) return;
    dispatch({
      type: 'SET_RULE_SELECTION',
      payload: {
        selectedRule: { ruleTitle: 'Quick Rules', ruleText: quickRulesContent, isQuick: true },
        selectedIndex: null,
        isQuickSelected: true,
      },
    });
    closeMobileMenu();
    resetScroll();
  }, [quickRulesContent, resetScroll, closeMobileMenu]);

  const formatQuickRules = useCallback((rules) => {
    if (!rules) return '';
    if (Array.isArray(rules)) return rules.join('\n');
    return String(rules);
  }, []);

  const handlePrevious = useCallback(() => {
    if (isQuickSelected && fareRules.length > 0) {
      handleRuleClick(fareRules[fareRules.length - 1], fareRules.length - 1);
      return;
    }
    if (selectedIndex === null || selectedIndex === 0) {
      if (quickRulesContent) {
        handleQuickRulesClick();
      }
      return;
    }
    handleRuleClick(fareRules[selectedIndex - 1], selectedIndex - 1);
  }, [
    isQuickSelected,
    fareRules,
    selectedIndex,
    quickRulesContent,
    handleRuleClick,
    handleQuickRulesClick,
  ]);

  const handleNext = useCallback(() => {
    if (isQuickSelected && fareRules.length > 0) {
      handleRuleClick(fareRules[0], 0);
      return;
    }
    if (selectedIndex === null || selectedIndex >= fareRules.length - 1) {
      if (quickRulesContent) {
        handleQuickRulesClick();
      }
      return;
    }
    handleRuleClick(fareRules[selectedIndex + 1], selectedIndex + 1);
  }, [
    isQuickSelected,
    fareRules,
    selectedIndex,
    quickRulesContent,
    handleRuleClick,
    handleQuickRulesClick,
  ]);

  const handleFareGroupTabClick = useCallback(
    (index) => {
      if (selectedPassengerTypeCode) {
        pendingPassengerTypeCodeRef.current = selectedPassengerTypeCode;
      }
      if (selectedRule && !isQuickSelected && selectedRule.ruleTitle) {
        pendingRuleTitleRef.current = selectedRule.ruleTitle;
      } else {
        pendingRuleTitleRef.current = null;
      }

      dispatch({ type: 'SET_SELECTED_FARE_GROUP_INDEX', payload: index });
      closeMobileMenu();
    },
    [selectedPassengerTypeCode, selectedRule, isQuickSelected, closeMobileMenu],
  );

  const handlePassengerTypeCodeChange = (newPassengerTypeCode) => {
    if (selectedRule && !isQuickSelected && selectedRule.ruleTitle) {
      pendingRuleTitleRef.current = selectedRule.ruleTitle;
    } else {
      pendingRuleTitleRef.current = null;
    }

    dispatch({ type: 'SET_SELECTED_PASSENGER_TYPE_CODE', payload: newPassengerTypeCode });
  };

  useEffect(() => {
    if (actualPassengerTypeCodes.length > 0) {
      const codes = actualPassengerTypeCodes.map((pt) => pt.code);
      if (
        pendingPassengerTypeCodeRef.current &&
        codes.includes(pendingPassengerTypeCodeRef.current)
      ) {
        dispatch({
          type: 'SET_SELECTED_PASSENGER_TYPE_CODE',
          payload: pendingPassengerTypeCodeRef.current,
        });
        pendingPassengerTypeCodeRef.current = null;
      } else if (!selectedPassengerTypeCode || !codes.includes(selectedPassengerTypeCode)) {
        dispatch({
          type: 'SET_SELECTED_PASSENGER_TYPE_CODE',
          payload: actualPassengerTypeCodes[0].code,
        });
      }
    }
  }, [actualPassengerTypeCodes, selectedPassengerTypeCode]);

  useEffect(() => {
    if (pendingRuleTitleRef.current) {
      const ruleTitle = pendingRuleTitleRef.current;
      pendingRuleTitleRef.current = null;

      const ruleStillExists = fareRules.some((rule) => rule && rule.ruleTitle === ruleTitle);
      if (ruleStillExists) {
        const sameRuleIndex = fareRules.findIndex((rule) => rule && rule.ruleTitle === ruleTitle);
        const foundRule = fareRules[sameRuleIndex];

        if (sameRuleIndex !== -1) {
          dispatch({
            type: 'SET_RULE_SELECTION',
            payload: {
              selectedRule: foundRule,
              selectedIndex: sameRuleIndex,
              isQuickSelected: false,
            },
          });
          resetScroll();
          return;
        }
      }
    }

    if (quickRulesContent) {
      dispatch({
        type: 'SET_RULE_SELECTION',
        payload: {
          selectedRule: { ruleTitle: 'Quick Rules', ruleText: quickRulesContent, isQuick: true },
          selectedIndex: null,
          isQuickSelected: true,
        },
      });
      resetScroll();
    } else if (fareRules.length > 0) {
      dispatch({
        type: 'SET_RULE_SELECTION',
        payload: {
          selectedRule: fareRules[0],
          selectedIndex: 0,
          isQuickSelected: false,
        },
      });
      resetScroll();
    } else {
      dispatch({
        type: 'SET_RULE_SELECTION',
        payload: {
          selectedRule: null,
          selectedIndex: null,
          isQuickSelected: false,
        },
      });
    }
  }, [fareRules, quickRulesContent, resetScroll]);

  useEffect(() => {
    if (showMobileMenu && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [showMobileMenu]);

  const getSelectedRuleDisplayText = useCallback(() => {
    if (!selectedRule) return 'Select rule';
    if (isQuickSelected) return 'Quick Rules';
    return `${selectedRule.rph}. ${selectedRule.ruleTitle}`;
  }, [selectedRule, isQuickSelected]);

  const getFareGroupTabKey = useCallback(
    (fareGroup, index) => `fare-group-${(fareGroup && fareGroup.fareRuleKey) || index}`,
    [],
  );

  const getPassengerTypeCodeLabel = useCallback(
    (code) => {
      const pt = actualPassengerTypeCodes.find((p) => p.code === code);
      return pt && pt.name;
    },
    [actualPassengerTypeCodes],
  );

  const minSelectWidth = useMemo(() => {
    const allPassengerTypes = new Map();
    airFareRulesList.forEach((fareGroup) => {
      if (fareGroup.fareRulesGroups) {
        fareGroup.fareRulesGroups.forEach((group) => {
          if (group.passengerTypes) {
            group.passengerTypes.forEach((pt) => {
              if (pt && pt.code && pt.name && !allPassengerTypes.has(pt.code)) {
                allPassengerTypes.set(pt.code, pt.name);
              }
            });
          }
        });
      }
    });

    if (allPassengerTypes.size === 0) return 'auto';

    const longestLabel = Array.from(allPassengerTypes.values()).reduce(
      (longest, label) => (label.length > longest.length ? label : longest),
      '',
    );

    return `${longestLabel.length * CHAR_WIDTH + DROPDOWN_PADDING + ICON_SPACE}px`;
  }, [airFareRulesList]);

  const getRuleKey = useCallback((rule, idx) => {
    const rph = (rule && rule.rph) || 'rule';
    return `${rph}-${idx}`;
  }, []);

  const showPassengerTypeCodeDropdown = actualPassengerTypeCodes.length >= 1;

  const formatFlightSegments = useCallback((flights) => {
    if (!flights || flights.length === 0) return '';
    return flights.map((flight) => `${flight.origin}→${flight.destination}`).join(', ');
  }, []);

  const fareGroupTabs = useMemo(
    () =>
      airFareRulesList.map((fareGroup, index) => ({
        key: getFareGroupTabKey(fareGroup, index),
        fareGroup,
        index,
      })),
    [airFareRulesList, getFareGroupTabKey],
  );

  if (!isOpen) return null;

  const hasAnyRules =
    airFareRulesList &&
    airFareRulesList.length > 0 &&
    airFareRulesList.some(
      (fareRule) =>
        fareRule.fareRulesGroups &&
        fareRule.fareRulesGroups.length > 0 &&
        fareRule.fareRulesGroups.some(
          (group) =>
            (group.quickRules && group.quickRules.length > 0) ||
            (group.fareRuleInfo && group.fareRuleInfo.length > 0),
        ),
    );

  if (!hasAnyRules) {
    return (
      <div className="fixed inset-0" style={{ zIndex: 99999 }}>
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-3"
          onClick={onClose}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="fare-rules-empty-title"
            tabIndex={-1}
            className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b">
              <h2 id="fare-rules-empty-title" className="text-base sm:text-lg font-bold">
                Fare Rules
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-6 text-center text-gray-700">
              No fare rules available for this flight.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0" style={{ zIndex: 99999 }}>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-3"
        onClick={onClose}
      >
        <div
          className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fare-rules-modal-title"
          tabIndex={-1}
          style={{ height: '95vh', maxHeight: '95vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b flex-shrink-0">
            <div className="flex-1 min-w-0">
              <h2 id="fare-rules-modal-title" className="text-lg sm:text-2xl font-bold truncate">
                Fare Rules
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-black text-2xl sm:text-3xl font-bold leading-none"
              type="button"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Fare Group tabs */}
          {airFareRulesList.length > 1 && (
            <div className="border-b flex-shrink-0 overflow-x-auto">
              <div className="flex">
                {fareGroupTabs.map(({ fareGroup, index }) => (
                  <button
                    key={getFareGroupTabKey(fareGroup, index)}
                    type="button"
                    onClick={() => handleFareGroupTabClick(index)}
                    className={`px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      selectedFareGroupIndex === index
                        ? 'border-theme text-theme bg-theme bg-opacity-10'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-semibold">Fare {index + 1}</span>
                    {fareGroup.flights && fareGroup.flights.length > 0 && (
                      <span className="text-gray-500 ml-2 text-xs">
                        ({formatFlightSegments(fareGroup.flights)})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Applicable flights banner */}
          <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-2 border-b bg-blue-50 flex-shrink-0 overflow-x-clip">
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-800">
              {showPassengerTypeCodeDropdown && (
                <>
                  <span className="font-semibold">Passenger Type:</span>
                  <div className="w-full sm:w-auto relative">
                    <button
                      type="button"
                      onClick={() =>
                        dispatch({
                          type: 'SET_SHOW_PASSENGER_TYPE_MENU',
                          payload: !showPassengerTypeMenu,
                        })
                      }
                      className="w-full sm:w-auto px-3 py-2 bg-gray-500 bg-opacity-5 rounded-lg font-semibold text-xs sm:text-sm flex items-center justify-between shadow"
                      style={{
                        minWidth: window.innerWidth >= BREAKPOINT_SM ? minSelectWidth : undefined,
                      }}
                      aria-haspopup="true"
                      aria-expanded={showPassengerTypeMenu}
                      aria-controls="passenger-type-menu"
                    >
                      <span>
                        {selectedPassengerTypeCode &&
                          getPassengerTypeCodeLabel(selectedPassengerTypeCode)}
                      </span>
                      <svg
                        className={`w-4 h-4 ml-2 transition-transform ${showPassengerTypeMenu ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {showPassengerTypeMenu && (
                      <div
                        id="passenger-type-menu"
                        role="menu"
                        className="absolute top-full left-0 right-0 sm:right-auto sm:min-w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                      >
                        {actualPassengerTypeCodes.map((pt) => (
                          <button
                            key={pt.code}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              handlePassengerTypeCodeChange(pt.code);
                              dispatch({ type: 'SET_SHOW_PASSENGER_TYPE_MENU', payload: false });
                            }}
                            className={`w-full text-left px-4 py-3 text-xs sm:text-sm border-b last:border-b-0 ${
                              pt.code === selectedPassengerTypeCode
                                ? 'bg-theme text-header-0 font-medium'
                                : 'text-gray-700 hover:bg-theme hover:bg-opacity-10'
                            }`}
                          >
                            {pt.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="hidden sm:inline text-gray-400 mx-2">|</span>
                </>
              )}
              <span className="font-semibold">These rules apply to: </span>
              {currentFareGroup &&
              currentFareGroup.flights &&
              currentFareGroup.flights.length > 0 ? (
                <span className="font-medium">
                  {formatFlightSegments(currentFareGroup.flights)}
                </span>
              ) : (
                <span className="text-gray-500 italic">No flight segments</span>
              )}
            </div>
          </div>

          {/* Mobile dropdown */}
          <div className="md:hidden border-b flex-shrink-0">
            <button
              type="button"
              onClick={() => dispatch({ type: 'SET_SHOW_MOBILE_MENU', payload: !showMobileMenu })}
              className="w-full px-4 py-3 flex items-center justify-between bg-theme text-header-0 font-medium"
            >
              <span className="text-sm truncate">{getSelectedRuleDisplayText()}</span>
              <svg
                className={`w-5 h-5 transition-transform ${showMobileMenu ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {/* Mobile content */}
          <div className="md:hidden flex-1 min-h-0 overflow-hidden">
            {showMobileMenu ? (
              <div ref={dropdownRef} className="h-full overflow-y-auto bg-white">
                {quickRulesContent && (
                  <>
                    <button
                      ref={isQuickSelected ? selectedItemRef : null}
                      type="button"
                      className={`w-full text-left px-4 py-3 border-b text-sm ${
                        isQuickSelected
                          ? 'bg-theme text-header-0 font-medium'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                      onClick={handleQuickRulesClick}
                    >
                      Quick Rules
                    </button>
                    <div className="border-t border-gray-200" />
                  </>
                )}
                {fareRules.map((rule, idx) => (
                  <button
                    ref={selectedIndex === idx && !isQuickSelected ? selectedItemRef : null}
                    type="button"
                    key={getRuleKey(rule, idx)}
                    className={`w-full text-left px-4 py-3 border-b text-sm ${
                      selectedIndex === idx && !isQuickSelected
                        ? 'bg-theme text-header-0 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    onClick={() => handleRuleClick(rule, idx)}
                  >
                    <span
                      className={`font-semibold mr-2 ${selectedIndex === idx && !isQuickSelected ? 'text-header-0 opacity-75' : 'text-theme'}`}
                    >
                      {rule.rph}
                    </span>
                    {rule.ruleTitle}
                  </button>
                ))}
              </div>
            ) : (
              <div className="h-full overflow-y-auto p-3">
                {selectedRule && (
                  <div className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700">
                    {isQuickSelected
                      ? formatQuickRules(selectedRule.ruleText)
                      : selectedRule.ruleText || 'No additional information available.'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop layout */}
          <div className="hidden md:flex gap-4 p-3 flex-1 min-h-0 overflow-hidden">
            <div className="w-1/3 border border-gray-200 flex flex-col h-full bg-white shadow-sm overflow-hidden">
              {quickRulesContent && (
                <>
                  <button
                    type="button"
                    className={`w-full text-left font-semibold p-3 flex-shrink-0 ${isQuickSelected ? 'bg-theme text-header-0' : 'text-theme hover:bg-theme hover:bg-opacity-10'}`}
                    onClick={handleQuickRulesClick}
                  >
                    Quick Rules
                  </button>
                  <div className="border-t border-gray-200" />
                </>
              )}
              <div className="flex-1 overflow-y-auto">
                {fareRules.map((rule, idx) => (
                  <div
                    key={getRuleKey(rule, idx)}
                    className={`p-3 cursor-pointer border-b border-gray-100 ${
                      selectedIndex === idx && !isQuickSelected
                        ? 'bg-theme text-header-0'
                        : 'hover:bg-theme hover:bg-opacity-10 text-gray-700'
                    }`}
                    onClick={() => handleRuleClick(rule, idx)}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`text-xs font-semibold ${selectedIndex === idx && !isQuickSelected ? 'text-header-0 opacity-75' : 'text-theme'}`}
                      >
                        {rule.rph}
                      </span>
                      <span className="text-sm leading-tight">{rule.ruleTitle}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-2/3 flex flex-col border border-gray-200 rounded-lg bg-white shadow-sm h-full overflow-hidden">
              {selectedRule ? (
                <>
                  <div className="px-4 py-2 border-b flex-shrink-0">
                    {isQuickSelected ? (
                      <h3 className="font-semibold text-lg text-gray-800">Quick Rules</h3>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center min-w-8 h-8 px-2 bg-theme text-header-0 rounded-full text-sm font-bold">
                          {selectedRule.rph}
                        </span>
                        <h3 className="font-semibold text-lg text-gray-800">
                          {selectedRule.ruleTitle}
                        </h3>
                      </div>
                    )}
                  </div>
                  <div ref={contentRef} className="flex-1 overflow-y-auto p-4">
                    <div className="whitespace-pre-wrap leading-relaxed text-gray-800">
                      {isQuickSelected
                        ? formatQuickRules(selectedRule.ruleText)
                        : selectedRule.ruleText || 'No additional information available.'}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-gray-500 text-center mt-8 text-sm">
                  Select a rule to view details
                </div>
              )}
            </div>
          </div>

          {/* Footer navigation */}
          <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-t bg-gray-50 gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handlePrevious}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              ← Previous
            </button>
            <span className="text-xs sm:text-sm text-gray-600 truncate text-center flex-1 hidden sm:inline">
              {selectedRule && selectedRule.ruleTitle}
            </span>
            <button
              type="button"
              onClick={handleNext}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

FlightFareRulesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  airFareRulesList: PropTypes.arrayOf(
    PropTypes.shape({
      fareRuleKey: PropTypes.string,
      flights: PropTypes.arrayOf(
        PropTypes.shape({
          origin: PropTypes.string,
          destination: PropTypes.string,
          bookingCode: PropTypes.string,
          departureDateTime: PropTypes.string,
        }),
      ),
      fareRulesGroups: PropTypes.arrayOf(
        PropTypes.shape({
          passengerTypes: PropTypes.arrayOf(
            PropTypes.shape({
              code: PropTypes.string,
              name: PropTypes.string,
              description: PropTypes.string,
            }),
          ),
          quickRules: PropTypes.arrayOf(PropTypes.string),
          fareRuleInfo: PropTypes.arrayOf(
            PropTypes.shape({
              rph: PropTypes.string,
              ruleTitle: PropTypes.string,
              ruleText: PropTypes.string,
            }),
          ),
        }),
      ),
    }),
  ).isRequired,
};

export default FlightFareRulesModal;
